// ============================================
// PlatformManager - 外部平台管理组件
// 完整生产级实现
// ============================================

import { useEffect, useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

interface Platform {
  id: string
  name: string
  base_url: string
  is_active: boolean
  last_sync_at?: string
  sync_status?: 'success' | 'failed' | 'pending'
  total_collected: number
  total_failed: number
  created_at: string
}

export default function PlatformManager() {
  const [platforms, setPlatforms] = useState<Platform[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchPlatforms()
  }, [])

  const fetchPlatforms = async () => {
    setLoading(true)
    try {
      const response = await fetch('/api/platform')
      const data = await response.json()
      if (data.success) {
        setPlatforms(data.data.platforms)
      }
    } catch (error) {
      console.error('Failed to fetch platforms:', error)
    } finally {
      setLoading(false)
    }
  }

  const triggerSync = async (platformId: string) => {
    try {
      const response = await fetch(`/api/platform/${platformId}/sync`, {
        method: 'POST'
      })
      const data = await response.json()
      if (data.success) {
        alert('同步任务已创建')
        fetchPlatforms()
      }
    } catch (error) {
      console.error('Failed to trigger sync:', error)
      alert('触发同步失败')
    }
  }

  if (loading) {
    return <div className="text-white">加载中...</div>
  }

  return (
    <div className="space-y-6">
      {/* 平台列表 */}
      <Card>
        <CardHeader>
          <CardTitle>外部平台管理</CardTitle>
          <CardDescription>配置和管理数据采集平台</CardDescription>
        </CardHeader>
        <CardContent>
          {platforms.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              暂无平台配置
            </div>
          ) : (
            <div className="space-y-4">
              {platforms.map((platform) => (
                <div
                  key={platform.id}
                  className="border border-slate-700 rounded-lg p-4 hover:bg-slate-700/30 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-white">
                          {platform.name}
                        </h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          platform.is_active
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-slate-600/20 text-slate-400'
                        }`}>
                          {platform.is_active ? '活跃' : '停用'}
                        </span>
                        {platform.sync_status && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            platform.sync_status === 'success'
                              ? 'bg-green-500/20 text-green-400'
                              : platform.sync_status === 'failed'
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {platform.sync_status === 'success' ? '同步成功' : platform.sync_status === 'failed' ? '同步失败' : '同步中'}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-400 mt-1">
                        {platform.base_url}
                      </p>

                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div>
                          <div className="text-xs text-slate-400">已采集</div>
                          <div className="text-lg font-semibold text-white">
                            {platform.total_collected.toLocaleString()}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-400">失败</div>
                          <div className="text-lg font-semibold text-red-400">
                            {platform.total_failed.toLocaleString()}
                          </div>
                        </div>
                        <div>
                          <div className="text-xs text-slate-400">成功率</div>
                          <div className="text-lg font-semibold text-green-400">
                            {platform.total_collected + platform.total_failed > 0
                              ? ((platform.total_collected / (platform.total_collected + platform.total_failed)) * 100).toFixed(1)
                              : 0}%
                          </div>
                        </div>
                      </div>

                      {platform.last_sync_at && (
                        <div className="mt-3 text-xs text-slate-400">
                          最后同步: {new Date(platform.last_sync_at).toLocaleString('zh-CN')}
                        </div>
                      )}
                    </div>

                    <div className="ml-4">
                      <Button
                        size="sm"
                        onClick={() => triggerSync(platform.id)}
                        disabled={!platform.is_active}
                      >
                        立即同步
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* 使用说明 */}
      <Card>
        <CardHeader>
          <CardTitle>添加新平台</CardTitle>
          <CardDescription>通过API添加外部数据源</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-slate-900 rounded-lg p-4 font-mono text-sm text-slate-300">
            <div className="text-slate-400 mb-2"># 示例：添加新平台</div>
            <div>POST /api/platform</div>
            <div className="mt-2">{`{`}</div>
            <div className="ml-4">"name": "XX省招标投标公共服务平台",</div>
            <div className="ml-4">"baseUrl": "https://api.example.com",</div>
            <div className="ml-4">"config": {`{`}</div>
            <div className="ml-8">"apiKey": "your-api-key",</div>
            <div className="ml-8">"apiVersion": "v1"</div>
            <div className="ml-4">{`}`}</div>
            <div>{`}`}</div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
